<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  ChevronDown,
  CircleCheck,
  Clock3,
  Filter,
  FolderKanban,
  ListFilter,
  Plus,
  Search,
  SlidersHorizontal,
} from "lucide-vue-next";
import AppShell from "@/components/workspace/AppShell.vue";
import CreateProjectFlow from "@/components/portfolio/CreateProjectFlow.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import { shortDate } from "@/utils/dates";

const props = defineProps<{ myWork?: boolean }>();
const workspace = useWorkspaceStore();
const route = useRoute();
const router = useRouter();
const createOpen = ref(false);
const query = ref("");
const statusFilter = ref<"all" | "active" | "on_hold" | "completed">("active");
const compactRows = ref(false);
const myWorkAttentionOnly = ref(true);
const visibleProjects = computed(() =>
  workspace.projects.filter(
    (project) =>
      `${project.name} ${project.client} ${project.code}`
        .toLowerCase()
        .includes(query.value.toLowerCase()) &&
      (statusFilter.value === "all" ||
        project.status.toLowerCase().replaceAll(" ", "_") ===
          statusFilter.value),
  ),
);
const decisionCount = computed(
  () =>
    workspace.projects.filter(
      (project) =>
        project.health === "At risk" || project.health === "Off track",
    ).length,
);
const onTrackCount = computed(
  () =>
    workspace.projects.filter((project) => project.health === "On track")
      .length,
);
const myTasks = computed(() =>
  workspace.myWork
    .filter(
      (task) =>
        !myWorkAttentionOnly.value ||
        ["At risk", "Blocked", "Overdue"].includes(task.health),
    )
    .slice(0, 10),
);

watchEffect(() => {
  const profile = sessionStorage.getItem("planner-demo-profile") as
    "internal" | "admin" | "client" | null;
  if (!workspace.user && profile) workspace.login(profile);
  if (!workspace.projects.length && workspace.user) workspace.projects = [];
  if (props.myWork && workspace.user) void workspace.loadMyWork();
});

function openProject(
  project: (typeof workspace.projects)[number],
  showScheduleAttention = false,
) {
  router.push({
    path: `/projects/${project.id}/list`,
    query: showScheduleAttention ? { attention: "1" } : {},
  });
}

function scheduleContext(project: (typeof workspace.projects)[number]) {
  if (project.blockers === 1) return "1 open blocker";
  if (project.blockers > 1) return `${project.blockers} open blockers`;
  return project.health === "On track"
    ? "Within project target"
    : project.health === "Off track"
      ? "Open to review overdue work"
      : "Open to review blockers";
}
</script>

<template>
  <AppShell>
    <div class="portfolio">
      <template v-if="props.myWork">
        <header class="portfolio__header">
          <div>
            <h1>My Work</h1>
            <p>
              Your assigned open work across the projects you can currently
              access.
            </p>
          </div>
        </header>
        <section class="work-sheet sheet">
          <header class="work-sheet__head">
            <div>
              <h2>Needs attention</h2>
              <p>Overdue, blocked, and at-risk work appears first.</p>
            </div>
            <button
              class="button button--secondary"
              type="button"
              :aria-pressed="myWorkAttentionOnly"
              @click="myWorkAttentionOnly = !myWorkAttentionOnly"
            >
              <Filter :size="16" />
              {{ myWorkAttentionOnly ? "Needs attention" : "All open work" }}
            </button>
          </header>
          <button
            v-for="task in myTasks"
            :key="task.id"
            class="my-task"
            type="button"
            @click="
              router.push({
                path: `/projects/${task.projectId}/list`,
                query: { task: task.id },
              })
            "
          >
            <span
              class="status-dot"
              :class="`status-dot--${task.health.toLowerCase().replace(' ', '-')}`"
            ></span
            ><span class="my-task__main"
              ><strong>{{ task.title }}</strong
              ><small
                >{{ task.projectName
                }}<span v-if="workspace.isInternal">
                  · {{ task.visibility }}</span
                ></small
              ></span
            ><span
              class="my-task__status badge"
              :class="
                task.status === 'Blocked' ? 'badge--blocked' : 'badge--progress'
              "
              >{{ task.status }}</span
            ><time class="tabular">{{ shortDate(task.dueDate) }}</time
            ><ArrowRight :size="17" />
          </button>
          <div v-if="!myTasks.length" class="work-empty">
            <CircleCheck :size="22" />
            <strong>No assigned work matches this view.</strong>
            <span>
              {{
                myWorkAttentionOnly
                  ? "Show all open work to review the rest of your assignments."
                  : "New assignments will appear here when they are added to a project you can access."
              }}
            </span>
          </div>
        </section>
      </template>

      <template v-else>
        <header class="portfolio__header">
          <div>
            <h1>Active projects</h1>
            <p>
              Start with the work that changed, slipped, or needs a decision.
            </p>
          </div>
          <button
            v-if="workspace.user?.canCreateProjects"
            class="button button--primary"
            type="button"
            @click="createOpen = true"
          >
            <Plus :size="17" /> Create project
          </button>
        </header>

        <section
          class="attention-strip"
          aria-label="Portfolio attention summary"
        >
          <div>
            <AlertTriangle :size="18" /><span
              ><strong>{{ decisionCount }}</strong>
              {{ decisionCount === 1 ? "project needs" : "projects need" }} a
              schedule decision</span
            >
          </div>
          <div>
            <Clock3 :size="18" /><span
              ><strong>{{ workspace.projects.length }}</strong> active
              {{ workspace.projects.length === 1 ? "project" : "projects" }} in
              your workspace</span
            >
          </div>
          <div>
            <CircleCheck :size="18" /><span
              ><strong>{{ onTrackCount }}</strong>
              {{ onTrackCount === 1 ? "project is" : "projects are" }} on
              track</span
            >
          </div>
          <button type="button" @click="router.push('/my-work')">
            Review My Work <ArrowRight :size="15" />
          </button>
        </section>

        <section class="project-sheet sheet">
          <header class="project-sheet__toolbar">
            <div class="project-search">
              <Search :size="16" /><input
                v-model="query"
                aria-label="Search projects"
                placeholder="Search active projects"
              />
            </div>
            <div>
              <label class="portfolio-status">
                <ListFilter :size="16" />
                <span class="sr-only">Filter projects by status</span>
                <select v-model="statusFilter">
                  <option value="active">Active</option>
                  <option value="on_hold">On hold</option>
                  <option value="completed">Completed</option>
                  <option value="all">All projects</option>
                </select>
                <ChevronDown :size="14" />
              </label>
              <button
                class="icon-button"
                type="button"
                :aria-label="
                  compactRows
                    ? 'Use comfortable project rows'
                    : 'Use compact project rows'
                "
                :aria-pressed="compactRows"
                @click="compactRows = !compactRows"
              >
                <SlidersHorizontal :size="18" />
              </button>
            </div>
          </header>
          <div class="project-table-head" aria-hidden="true">
            <span>Project</span><span>Schedule</span><span>Progress</span
            ><span>Next milestone</span><span>Updated</span><span></span>
          </div>
          <div
            v-for="project in visibleProjects"
            :key="project.id"
            class="project-row"
            :class="{ 'project-row--compact': compactRows }"
          >
            <button
              class="project-row__open"
              type="button"
              :aria-label="`Open ${project.name}`"
              @click="openProject(project)"
            ></button>
            <span class="project-identity"
              ><span class="client-monogram">
                <img
                  v-if="project.clientOrganizationLogoDataUrl"
                  :src="project.clientOrganizationLogoDataUrl"
                  :alt="`${project.client} logo`"
                />
                <template v-else>{{
                  project.client.slice(0, 2).toUpperCase()
                }}</template></span
              ><span class="project-identity__text"
                ><strong>{{ project.name }}</strong
                ><small>{{ project.code }} · {{ project.client }}</small></span
              ></span
            >
            <button
              class="project-field project-schedule project-schedule-action"
              type="button"
              :aria-label="`${project.health} schedule health. ${scheduleContext(project)}.`"
              @click="openProject(project, project.health !== 'On track')"
            >
              <small class="project-field__label">Schedule</small
              ><span
                class="badge"
                :class="
                  project.health === 'At risk'
                    ? 'badge--risk'
                    : project.health === 'Off track'
                      ? 'badge--blocked'
                      : 'badge--complete'
                "
                >{{ project.health }}</span
              ><small>{{ scheduleContext(project) }}</small>
            </button>
            <span class="project-field project-progress"
              ><small class="project-field__label">Progress</small
              ><span class="project-progress__body"
                ><strong class="tabular">{{ project.progress }}%</strong
                ><span class="progress-track" aria-hidden="true"
                  ><span
                    :style="{ width: `${project.progress}%` }"
                  ></span></span
              ></span>
            </span>
            <span class="project-field project-milestone"
              ><small class="project-field__label">Next milestone</small
              ><strong>{{ project.nextMilestone }}</strong
              ><small
                ><CalendarDays :size="13" />
                {{ shortDate(project.nextMilestoneDate) }}</small
              ></span
            >
            <span class="project-field project-updated"
              ><small class="project-field__label">Updated</small
              ><span>{{ project.updatedAt }}</span
              ><small>by project team</small></span
            >
            <ArrowRight
              class="project-row__arrow"
              :size="17"
              aria-hidden="true"
            />
          </div>
          <div v-if="!visibleProjects.length" class="project-empty">
            <FolderKanban :size="26" /><strong
              >No projects match this search.</strong
            ><span>Clear the search to return to your active projects.</span
            ><button
              class="button button--secondary"
              type="button"
              @click="query = ''"
            >
              Clear search
            </button>
          </div>
        </section>
      </template>
    </div>
    <CreateProjectFlow :open="createOpen" @close="createOpen = false" />
  </AppShell>
</template>

<style scoped>
.portfolio {
  width: min(1440px, 100%);
  margin: 0 auto;
  padding: 30px 34px 60px;
}
.portfolio__header {
  min-height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}
.portfolio__header p {
  margin-top: 6px;
  color: var(--text-soft);
  font-size: 15px;
}
.attention-strip {
  min-height: 66px;
  margin: 20px 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  align-items: center;
  border-block: 1px solid var(--rule-strong);
  color: var(--text-soft);
}
.attention-strip > div {
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 18px;
  border-right: 1px solid var(--rule);
}
.attention-strip > div:first-child {
  padding-left: 0;
  color: #795114;
}
.attention-strip > div:nth-child(2) svg {
  color: var(--shared);
}
.attention-strip > div:nth-child(3) svg {
  color: var(--success);
}
.attention-strip strong {
  color: var(--text);
}
.attention-strip button {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: var(--brand-strong);
  font-weight: 700;
  cursor: pointer;
}
.project-sheet {
  overflow: hidden;
  container-name: project-list;
  container-type: inline-size;
}
.project-sheet__toolbar {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--rule);
}
.project-sheet__toolbar > div {
  display: flex;
  gap: 8px;
}
.portfolio-status {
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: white;
  color: var(--text-soft);
}
.portfolio-status select {
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text);
  appearance: none;
}
.project-search {
  width: min(420px, 50vw);
  height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: var(--sheet-subtle);
  color: var(--text-faint);
}
.project-search:focus-within {
  border-color: var(--focus);
  box-shadow: 0 0 0 3px rgba(31, 112, 193, 0.12);
}
.project-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  background: transparent;
}
.project-table-head,
.project-row {
  display: grid;
  grid-template-columns:
    minmax(220px, 1.55fr) minmax(140px, 0.8fr) minmax(135px, 0.75fr)
    minmax(175px, 1fr) minmax(120px, 0.65fr) 24px;
  align-items: center;
  gap: clamp(12px, 1.35vw, 20px);
}
.project-row--compact {
  min-height: 62px;
  padding-block: 7px;
}
.project-table-head {
  min-height: 36px;
  padding: 0 18px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet-subtle);
  color: var(--text-faint);
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.project-row {
  position: relative;
  width: 100%;
  min-height: 82px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--rule);
  background: white;
  text-align: left;
}
.project-row__open {
  position: absolute;
  z-index: 0;
  inset: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  cursor: pointer;
}
.project-row > * {
  min-width: 0;
}
.project-row > :not(.project-row__open) {
  position: relative;
  z-index: 1;
  pointer-events: none;
}
.project-row > .project-schedule-action {
  pointer-events: auto;
}
.project-row:last-of-type {
  border-bottom: 0;
}
.project-row:hover {
  background: var(--sheet-subtle);
}
.project-row:has(.project-row__open:focus-visible) {
  z-index: 1;
  box-shadow:
    inset 2px 0 0 var(--focus),
    0 0 0 3px rgba(75, 127, 55, 0.24);
}
.project-row__open:focus-visible {
  outline: 0;
}
.client-monogram {
  overflow: hidden;
}
.client-monogram img {
  width: 100%;
  height: 100%;
  padding: 3px;
  object-fit: contain;
}
.project-field {
  display: grid;
  gap: 5px;
}
.project-schedule-action {
  border-radius: 4px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.project-schedule-action:hover {
  background: var(--sheet-strong);
}
.project-row .project-field__label {
  display: none;
  color: var(--text-faint) !important;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}
.project-row small {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-soft);
}
.project-identity {
  display: flex;
  align-items: center;
  gap: 12px;
}
.project-identity__text {
  display: grid;
  gap: 4px;
}
.project-identity__text strong,
.project-milestone strong {
  overflow-wrap: anywhere;
}
.client-monogram {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: 1px solid var(--rule);
  border-radius: 6px;
  color: var(--ink-800);
  background: var(--sheet-subtle);
  font-size: 11px;
  font-weight: 800;
}
.project-progress {
  min-width: 0;
}
.project-progress__body {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 9px;
}
.project-progress__body .progress-track {
  min-width: 54px;
}
.project-row__arrow {
  color: var(--text-faint);
}
.project-empty,
.placeholder-sheet {
  display: grid;
  place-items: center;
  gap: 9px;
  min-height: 340px;
  padding: 38px;
  text-align: center;
}
.project-empty {
  color: var(--text-soft);
}
.project-empty strong {
  color: var(--text);
}
.project-empty .button {
  margin-top: 6px;
}
.placeholder-sheet {
  margin-top: 24px;
}
.placeholder-sheet > p {
  max-width: 600px;
  color: var(--text-soft);
}
.placeholder-mark {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.work-sheet {
  margin-top: 24px;
  overflow: hidden;
}
.work-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid var(--rule);
}
.work-sheet__head p {
  color: var(--text-soft);
}
.my-task {
  width: 100%;
  min-height: 62px;
  display: grid;
  grid-template-columns: 12px 1fr auto 90px 22px;
  align-items: center;
  gap: 13px;
  padding: 10px 18px;
  border-bottom: 1px solid var(--rule);
  background: white;
  text-align: left;
  cursor: pointer;
}
.my-task:hover {
  background: var(--sheet-subtle);
}
.my-task__main {
  display: grid;
}
.my-task__main small {
  color: var(--text-soft);
}
.work-empty {
  min-height: 220px;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 7px;
  padding: 28px;
  color: var(--text-soft);
  text-align: center;
}
.work-empty strong {
  color: var(--text);
}
.work-empty span {
  max-width: 52ch;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--success);
}
.status-dot--at-risk {
  background: var(--warning);
}
.status-dot--blocked,
.status-dot--overdue {
  background: var(--danger);
}
@media (max-width: 1050px) {
  .attention-strip {
    grid-template-columns: 1fr 1fr;
    padding: 10px 0;
  }
  .attention-strip > div {
    border-right: 0;
  }
}
@container project-list (max-width: 1050px) {
  .project-table-head {
    display: none;
  }
  .project-row {
    grid-template-areas:
      "identity identity schedule arrow"
      "progress milestone updated arrow";
    grid-template-columns:
      minmax(180px, 1.05fr) minmax(160px, 0.85fr) minmax(135px, 0.65fr)
      20px;
    align-items: start;
    gap: 14px clamp(16px, 2.4cqi, 26px);
    min-height: 0;
    padding: 16px 18px;
  }
  .project-row--compact {
    padding-block: 12px;
  }
  .project-identity {
    grid-area: identity;
  }
  .project-schedule {
    grid-area: schedule;
  }
  .project-progress {
    grid-area: progress;
  }
  .project-milestone {
    grid-area: milestone;
  }
  .project-updated {
    grid-area: updated;
  }
  .project-row__arrow {
    grid-area: arrow;
    align-self: center;
  }
  .project-row .project-field__label {
    display: block;
  }
}
@container project-list (max-width: 650px) {
  .project-sheet__toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .project-search {
    width: 100%;
  }
  .project-row {
    grid-template-areas:
      "identity arrow"
      "schedule progress"
      "milestone milestone"
      "updated updated";
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 14px 18px;
    padding: 15px 14px;
  }
  .project-identity {
    grid-column: 1 / -1;
    padding-right: 26px;
  }
  .project-row__arrow {
    justify-self: end;
    align-self: center;
  }
  .project-milestone {
    padding-top: 13px;
    border-top: 1px solid var(--rule);
  }
  .project-updated {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 3px 6px;
    color: var(--text-soft);
  }
  .project-updated .project-field__label {
    flex-basis: 100%;
  }
  .project-updated small:last-child::before {
    content: "·";
    margin-right: 6px;
  }
}
@media (max-width: 700px) {
  .portfolio {
    padding: 22px 14px 40px;
  }
  .attention-strip {
    grid-template-columns: 1fr;
  }
  .attention-strip > div {
    padding: 7px 0;
  }
  .attention-strip button {
    padding: 10px 0;
  }
  .portfolio__header {
    align-items: flex-start;
  }
  .my-task {
    grid-template-columns: 10px 1fr auto;
  }
  .my-task time,
  .my-task > svg,
  .my-task__status {
    display: none;
  }
}
</style>
